import { Tag } from '@carbon/react';
import { ConfidenceBreakdown } from 'components/confidence-breakdown';
import { EntityLink } from 'components/entity-link';
import { ACTIVITY_SOURCE_LABEL } from 'lib/entities';
import { formatCellValue } from 'components/data-table';
import { EMPTY_CELL } from 'utils/empty-cell';
import { formatConfidence } from 'utils/number';
import {
  ACTION_OUTCOME_LABELS,
  ACTION_SOURCE_LABELS,
  fieldLabel,
  labelFor,
  ROLE_LABELS,
} from './utils';
import type { CleanupLogRow } from './types';
import { MetaItem } from '../meta-item';
import shared from '../admin-log.module.scss';

interface CleanupDetailProps {
  row: CleanupLogRow;
}

/**
 * Expanded-row body: the full record for one clean-up action, grouped, with the
 * per-factor score breakdown that does not fit a cell.
 * @param {CleanupDetailProps} props - The log row to expand
 * @return {JSX.Element} The row's detail
 */
export function CleanupDetail({ row }: CleanupDetailProps) {
  return (
    <div className={shared.expandedDetail}>
      <div className={shared.detailMeta}>
        <MetaItem label={fieldLabel('source')}>
          <Tag type="blue" size="sm">
            {ACTIVITY_SOURCE_LABEL[row.source]}
          </Tag>
        </MetaItem>
        <MetaItem label={fieldLabel('appContext')}>
          {row.appContext ?? EMPTY_CELL}
        </MetaItem>
        <MetaItem label={fieldLabel('actor')}>
          {row.actor ?? EMPTY_CELL}
        </MetaItem>
        <MetaItem label={fieldLabel('actorType')}>
          {row.actorType ?? EMPTY_CELL}
        </MetaItem>
        <MetaItem label={fieldLabel('actionAt')}>
          {formatCellValue(row.actionAt, { type: 'datetime' })}
        </MetaItem>
        <MetaItem label={fieldLabel('actionOutcome')}>
          {labelFor(ACTION_OUTCOME_LABELS, row.actionOutcome) ?? EMPTY_CELL}
        </MetaItem>
        <MetaItem label={fieldLabel('actionSource')}>
          {labelFor(ACTION_SOURCE_LABELS, row.actionSource) ?? EMPTY_CELL}
        </MetaItem>
        <MetaItem label={fieldLabel('role')}>{ROLE_LABELS[row.role]}</MetaItem>
        <MetaItem label={fieldLabel('entityName')}>
          <EntityLink id={row.entityId} name={row.entityName} />
          <span className={shared.muted}> · {row.entityType}</span>
        </MetaItem>
        <MetaItem label={fieldLabel('survivorName')}>
          <EntityLink
            id={row.survivorId}
            name={row.survivorName}
            fallback={EMPTY_CELL}
          />
          {row.survivorType && (
            <span className={shared.muted}> · {row.survivorType}</span>
          )}
        </MetaItem>
        <MetaItem label={fieldLabel('entityCreatedAt')}>
          {formatCellValue(row.entityCreatedAt, { type: 'datetime' })}
        </MetaItem>
        <MetaItem label={fieldLabel('confidence')}>
          {formatConfidence(row.confidence, EMPTY_CELL)}
        </MetaItem>
        <MetaItem label={fieldLabel('autoMatched')}>
          {row.autoMatched ? 'Yes' : 'No'}
        </MetaItem>
        <MetaItem label={fieldLabel('county')}>
          {row.county.length ? row.county.join(', ') : EMPTY_CELL}
        </MetaItem>
      </div>

      <h3 className={shared.sectionTitle}>{fieldLabel('confidenceFactors')}</h3>
      {row.confidenceFactors.length ? (
        <ConfidenceBreakdown factors={row.confidenceFactors} />
      ) : (
        <p className={shared.muted}>No scoring details recorded.</p>
      )}

      {row.reversed && (
        <>
          <h3 className={shared.sectionTitle}>Undo</h3>
          <div className={shared.detailMeta}>
            <MetaItem label={fieldLabel('reversed')}>Yes</MetaItem>
            <MetaItem label={fieldLabel('undoneAt')}>
              {formatCellValue(row.undoneAt, { type: 'datetime' })}
            </MetaItem>
            <MetaItem label={fieldLabel('undoneBy')}>
              {row.undoneBy ?? EMPTY_CELL}
            </MetaItem>
          </div>
        </>
      )}
    </div>
  );
}
